<template>
  <div>
    <loading-screen v-if="isLoading" />
    <uploaded-labels
      v-if="hasUploadedFile"
      :file-name="fileName"
      :workbook="workbook"
      @finished="finishedUploading"
    />
    <div
      v-else
      class="p-6 text-gray-900 bg-white rounded-lg text-left font-medium shadow-lg"
    >
      <h1 class="font-semibold text-xl">
        <fa-icon
          :icon="['fas', 'tag']"
          size="lg"
          class="mr-1 text-blue-400"
        />
        {{ $t("Custom labels", "Custom labels") }}
      </h1>
      <div class="mt-4 p-4 text-center">
        <search-bar v-model="searchPhrase" />
        <custom-label-filters />
        <div class="flex">
          <z-button
            :type="'primary'"
            :text="$t('New custom label', 'New custom label')"
            @click="showNewCustomLabelPopup = true"
          />
          <z-button
            v-tooltip.bottom="$t('Upload custom labels tooltip', 'When uploading xlsx, please include the following columns: Custom Label name, Synonyms, Exclude Phrases and Project. Synonyms, Exclusions and Projects should be separated by commas.')"
            :type="'primary'"
            :text="$t('Upload custom labels', 'Upload xlsx')"
            @click="openDialog"
          />
          <input
            ref="file"
            type="file"
            name="file"
            accept=".xls, .xlsx"
            style="display: none;"
            @change="uploadExcel"
          >
          <z-button
            :text="$t('Export xlsx', 'Export xlsx')"
            @click="exportXlsx"
          />
        </div>
        <progress-bar
          v-if="isDeleting"
          :all="deleteCount"
          :completed="deleteFinishedCount"
          class="mt-4"
        />
        <div>
          <div
            v-if="selectedCustomLabels.length === 0"
            class="w-full flex border border-gray-400 bg-gray-200 font-normal p-2 mt-3 text-sm"
          >
            <div class="w-1/12">
              <fa-icon
                :icon="['fal', 'circle']"
                size="lg"
                class="mr-1 text-blue-400 cursor-pointer"
                @click.stop="selectAll"
              />
            </div>
            <div
              class="w-3/12 text-left cursor-pointer"
              @click="handleSorting('name')"
            >
              {{ $t("Name", "Name") }}
              <fa-icon
                v-if="sort.column === 'name'"
                class="fa-icon sorting-icon"
                :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
              />
            </div>
            <div
              class="w-2/12 text-left cursor-pointer"
              @click="handleSorting('synonyms')"
            >
              {{ $t("Synonyms", "Synonyms") }}
              <fa-icon
                v-if="sort.column === 'synonyms'"
                class="fa-icon sorting-icon"
                :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
              />
            </div>
            <div
              class="w-2/12 text-left cursor-pointer"
              @click="handleSorting('author')"
            >
              {{ selectedCustomLabels.length > 1 ? '' : $t("Author", "Author") }}
              <fa-icon
                v-if="sort.column === 'author'"
                class="fa-icon sorting-icon"
                :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
              />
            </div>
            <div class="w-2/12 text-left">
              {{ selectedCustomLabels.length > 1 ? '' : $t("Projects", "Projects") }}
            </div>
            <div class="w-2/12 text-right" />
          </div>
          <div
            v-else
            class="w-full flex border border-gray-400 bg-gray-200 font-normal p-2 mt-3 text-sm"
          >
            <div class="w-1/12">
              <fa-icon
                :icon="['fas', 'circle']"
                size="lg"
                class="mr-1 text-blue-400 cursor-pointer"
                @click.stop="deselectAll"
              />
            </div>
            <div class="w-5/12 text-left">
              {{ selectedCustomLabels.length + '/' + customLabels.length + ' ' + $t("selected", "selected") }}
            </div>
            <div class="w-3/12 text-right text-blue-600 cursor-pointer">
              <vue-blob-json-csv
                file-type="json"
                file-name="custom_labels"
                :data="downloadableData"
              >
                {{ $t("Download JSON", "Download JSON") }}
              </vue-blob-json-csv>
            </div>
            <div
              class="w-3/12 text-right text-red-600 cursor-pointer"
              @click="deleteCustomLabels()"
            >
              {{ $t("Delete phrases ", "Delete phrases") }}
            </div>
          </div>
          <div
            v-for="(customLabel, index) in filteredCustomLabels"
            :key="index"
            class="w-full flex font-normal border-t border-gray-400 cursor-pointer text-sm"
          >
            <custom-label
              :custom-label="customLabel"
              :is-selected="selectedCustomLabels.indexOf(customLabel.id) > -1"
              @show="showUpdatePopup(customLabel)"
              @select-clicked="handleSelection($event, customLabel.id)"
            />
          </div>
        </div>
      </div>
    </div>
    <custom-label-popup
      v-if="showNewCustomLabelPopup === true"
      :title="selectedCustomLabel ? $t('Edit custom label', 'Edit custom label') : $t('Create a new custom label', 'Create a New Custom Label')"
      :custom-label="selectedCustomLabel"
      :all-projects="projects"
      @close="closePopup()"
    />
    <div
      v-if="showNewCustomLabelPopup"
      class="fixed z-20 top-0 left-0 bottom-0 right-0 bg-white opacity-75"
    />
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faTag, faSearch, faCircle, faSortUp, faSortDown } from '@fortawesome/pro-solid-svg-icons'
import { faCircle as LighCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Button from '../global/ui/Button'
import SearchBar from '../global/ui/SearchBar'
import { mapState, mapGetters } from 'vuex'
import { actionNames } from '@/custom-labels/store'
import { actionNames as projectActionNames } from '@/projects/store'
import CustomLabel from '@/custom-labels/components/CustomLabel'
import CustomLabelPopup from '@/custom-labels/components/CustomLabelPopup'
import UploadedLabels from '@/custom-labels/components/UploadedLabels'
import * as XLSX from 'xlsx'
import excelWriter from '@/utils/excelWriter'
import CustomLabelFilters from '@/custom-labels/components/CustomLabelFilters'
import PQueue from 'p-queue'
import ProgressBar from '@/global/ui/ProgressBar'
import LoadingScreen from '@/global/ui/LoadingScreen'

library.add(faTag, faSearch, faCircle, faSortUp, faSortDown, LighCircle)
export default {
  name: 'CustomLabels',
  components: {
    CustomLabelFilters,
    CustomLabelPopup,
    CustomLabel,
    UploadedLabels,
    SearchBar,
    'fa-icon': FontAwesomeIcon,
    'z-button': Button,
    ProgressBar,
    LoadingScreen
  },
  data () {
    return {
      searchPhrase: '',
      showNewCustomLabelPopup: false,
      selectedCustomLabel: null,
      selectedCustomLabels: [],
      workbook: {},
      fileName: '',
      hasUploadedFile: false,
      sort: {
        direction: '',
        column: null
      },
      isDeleting: false,
      deleteCount: 0,
      deleteFinishedCount: 0,
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      authors: state => state.customLabels.authors,
      projects: state => state.projects.projects
    }),
    ...mapGetters({ customLabels: 'filteredCustomLabels' }),
    downloadableData () {
      return this.customLabels.filter(cl => this.selectedCustomLabels.includes(cl.id)).map((cl) => {
        return {
          name: cl.name,
          synonyms: cl.synonyms,
          excludes: cl.excludes
        }
      })
    },
    filteredCustomLabels () {
      const customLabelsWithAuthors = this.customLabels.map((cl) => {
        return {
          ...cl,
          author: this.authors.find(a => a.id === cl.creatorId) ? this.authors.find(a => a.id === cl.creatorId).fullName : this.$t('Unknown User', 'Unknown User')
        }
      })
      let allCustomLabels = [...customLabelsWithAuthors]
      if (this.sort.column) {
        if (this.sort.column === 'synonyms') {
          allCustomLabels = allCustomLabels.sort((a, b) => a[this.sort.column].length - b[this.sort.column].length)
        } else {
          allCustomLabels = allCustomLabels.sort((a, b) => a[this.sort.column].localeCompare(b[this.sort.column]))
        }
        if (this.sort.direction === 'desc') {
          allCustomLabels = allCustomLabels.reverse()
        }
      }
      return allCustomLabels.filter(customLabel => {
        return customLabel.name.toLowerCase().includes(this.searchPhrase.toLowerCase())
      })
    }
  },
  async mounted () {
    this.isLoading = true
    await this.$store.dispatch(actionNames.GET_AUTHORS)
    await this.$store.dispatch(actionNames.GET_CUSTOM_LABELS)
    await this.$store.dispatch(projectActionNames.INIT_PROJECTS)
    this.isLoading = false
  },
  methods: {
    showUpdatePopup (customLabel) {
      this.selectedCustomLabel = customLabel
      this.showNewCustomLabelPopup = true
    },
    closePopup () {
      this.showNewCustomLabelPopup = false
      this.selectedCustomLabel = null
    },
    selectAll () {
      this.selectedCustomLabels = this.customLabels.map(cl => cl.id)
    },
    deselectAll () {
      this.selectedCustomLabels = []
    },
    handleSelection (isSelected, id) {
      if (isSelected) {
        this.selectedCustomLabels = [...this.selectedCustomLabels, id]
      } else {
        this.selectedCustomLabels = this.selectedCustomLabels.filter(cl => cl !== id)
      }
    },
    async deleteCustomLabels () {
      const accepted = await this.$swal('warning', {
        title: this.$t('Delete Custom Labels', 'Delete Custom Labels'),
        text: this.$t('Delete Custom Label Text', 'Do you want to delete these custom labels?'),
        icon: 'error',
        dangerMode: true,
        buttons: true
      })
      if (!accepted) {
        return
      }
      try {
        this.deleteCount = this.selectedCustomLabels.length
        this.isDeleting = true
        const queue = new PQueue({ concurrency: 4 })
        this.selectedCustomLabels.forEach(cl => queue.add(async () => {
          await this.$store.dispatch(actionNames.DELETE_CUSTOM_LABEL, cl)
          this.deleteFinishedCount++
          if (this.deleteFinishedCount === this.deleteCount) {
            this.isDeleting = false
            this.deleteFinishedCount = 0
            this.deleteCount = 0
          }
        }))
        this.selectedCustomLabels = []
      } catch (e) {
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },

    openDialog () {
      this.$refs.file.click()
    },
    async uploadExcel () {
      try {
        const file = this.$refs.file.files[0]
        if (file.name.slice(-3) === 'xls' || file.name.slice(-4) === 'xlsx') {
          const reader = new FileReader()
          reader.onload = async (e) => {
            const workbook = XLSX.read(e.target.result, { type: 'binary' })
            for (var i = 0; i < Object.keys(workbook.Sheets).length; i++) {
              this.workbook[Object.keys(workbook.Sheets)[i]] = XLSX.utils.sheet_to_json(workbook.Sheets[Object.keys(workbook.Sheets)[i]], {
                raw: false,
                header: 1,
                blankrows: false
              })
            }
            this.fileName = file.name
            this.hasUploadedFile = true
          }
          reader.readAsBinaryString(file)
        } else {
          this.$toast.error(this.$t('ANALYSIS.DROP_FILE_TYPE_ERROR'))
        }
      } catch (e) {
        this.$toast.error(this.$t('GENERAL.SOMETHING_WENT_WRONG', 'Something went wrong!'))
      }
      this.$refs.file.value = null
    },
    finishedUploading () {
      this.hasUploadedFile = false
      this.fileName = ''
      this.workbook = {}
    },
    async exportXlsx () {
      try {
        const headers = ['Name', 'Synonyms', 'Excludes', 'Projects']
        const data = this.customLabels.map(cl => [
          cl.name,
          cl.synonyms ? cl.synonyms.join(', ') : '',
          cl.excludes ? cl.excludes.join(', ') : '',
          cl.projects ? cl.projects.reduce((prev, p) => prev ? (prev + ', ' + p.name) : p.name, '') : ''
        ])
        await excelWriter({ fileName: 'custom_labels_export.xlsx', headers: headers, data: data })
      } catch (e) {
        console.error(e)
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },
    handleSorting (column) {
      if (column === this.sort.column) {
        switch (this.sort.direction) {
          case '':
            this.sort.direction = 'desc'
            break
          case 'desc':
            this.sort.direction = 'asc'
            break
          default:
            this.sort.direction = ''
            this.sort.column = null
            break
        }
      } else {
        this.sort.column = column
        this.sort.direction = 'desc'
      }
    }
  }
}
</script>
